import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
  Svg,
  Rect,
} from "@react-pdf/renderer";
import { PDFViewer } from "@react-pdf/renderer";

// --- FONT REGISTRATION (Important for consistent rendering) ---
Font.register({
  family: "Helvetica",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyC0IT0kLW-43aMEzIO6XUTLjad8.woff2",
    },
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyC0IT0kLW-43aMEzIO6XUTLjad8.woff2",
      fontWeight: "bold",
    },
  ],
});

// --- HELPER COMPONENTS (Logo & Barcode) ---
const DarazLogo = () => (
  <View style={{ flexDirection: "row", alignItems: "center" }}>
    <Text style={{ fontSize: 60, fontWeight: "bold", marginLeft: 15 }}>
      Lotus
    </Text>
  </View>
);

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString || dateString === "N/A") return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    return "N/A";
  }
};

const StaticBarcode = () => {
  const pattern =
    "11101101110011101011011100111010110111001110101101110011101011011100111010110111001110101101110011101011011100111010110111001110101101110011101011011100111010110111001110101101110011101011011100";
  return (
    <Svg
      width="100%"
      height="120"
      viewBox="0 0 480 70"
      preserveAspectRatio="none"
    >
      {pattern.split("").map((bar, index) => (
        <Rect
          key={index}
          x={index * 2.5}
          y="0"
          width={2.5}
          height="70"
          fill={bar === "1" ? "black" : "white"}
        />
      ))}
    </Svg>
  );
};

// --- COMPONENT FOR A SINGLE PAGE/LABEL ---
const SingleLabelPage = ({ order, companyDetails }) => {
  // Mock data for any missing fields to prevent errors
  const safeOrder = {
    sales_order: "sales_order",
    marketplace: "marketplace",
    tracking_number: order?.tracking_id || order?.id,
    routing_code: order?.id,
    service: "HOME",
    weight: "0.5kg",
    delivery_type: "Standard",
    payment_method: order?.payment_method,
    currency: "PKR",
    total_amount: order?.total_amount,
    order_number: order?.id,
    created_at: order?.created_at,
    awb_print_date: order?.created_at,
    courier: order?.courier_company || "Lotus",
    recipient: {
      name: order?.name,
      address_line1: "",
      address_line2: "",
      city_line: "",
      area: "",
      phone: "",
    },
    sender: {
      name: "N/A",
      address_line1: "",
      address_line2: "",
      address_line3: "",
      phone: "",
    },
    ...order,
  };

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${safeOrder.order_number}&qzone=1&margin=0`;

  const styles = StyleSheet.create({
    page: { backgroundColor: "#ffffff", fontFamily: "Helvetica", padding: 20 },
    container: { flex: 1, border: "3pt solid black", flexDirection: "column" },
    headerRow: {
      flexDirection: "row",
      borderBottom: "3pt solid black",
      height: 45,
    },
    headerCellLeft: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      borderRight: "3pt solid black",
    },
    headerCellRight: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    headerText: { fontSize: 18 },
    barcodeSection: {
      height: 150,
      borderBottom: "3pt solid black",
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 20,
      paddingTop: 10,
    },
    trackingText: { fontSize: 18, fontWeight: "bold", marginTop: 5 },
    middleRow: {
      flexDirection: "row",
      height: 200,
      borderBottom: "3pt solid black",
    },
    logoColumn: {
      width: "60%",
      borderRight: "3pt solid black",
      justifyContent: "space-around",
      alignItems: "center",
      paddingVertical: 20,
    },
    routingCode: { fontSize: 28, fontWeight: "bold", letterSpacing: 2 },
    detailsColumn: { width: "40%", flexDirection: "column" },
    detailCell: {
      flex: 1,
      borderBottom: "2pt solid black",
      justifyContent: "center",
      alignItems: "center",
    },
    detailCellLast: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 25,
    },
    detailText: { fontSize: 18, fontWeight: "bold" },
    orderRow: {
      height: 45,
      borderBottom: "3pt solid black",
      justifyContent: "center",
      alignItems: "center",
    },
    orderText: { fontSize: 16 },
    boldText: { fontWeight: "bold" },
    datesRow: {
      flexDirection: "row",
      height: 45,
      borderBottom: "3pt solid black",
    },
    dateLeft: {
      flex: 1,
      borderRight: "3pt solid black",
      justifyContent: "center",
      paddingLeft: 20,
    },
    dateRight: { flex: 1, justifyContent: "center", paddingLeft: 20 },
    dateText: { fontSize: 14 },
    bottomSection: { flexDirection: "row", flex: 1 },
    qrSection: {
      width: 250,
      borderRight: "3pt solid black",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: 20,
      paddingBottom: 15,
    },
    qrCode: { width: 200, height: 200 },
    courierText: { fontSize: 22, fontWeight: "bold" },
    addressSection: { flex: 1, padding: 20 },
    addressBlock: { fontSize: 16, lineHeight: 1.5 },
    addressLabel: { fontWeight: "bold" },
    dividerLine: { borderBottom: "2pt solid black", marginVertical: 15 },
  });

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.container} wrap={false}>
        <View style={styles.headerRow}>
          <View style={styles.headerCellLeft}>
            <Text style={styles.headerText}>{"sales_order"}</Text>
          </View>
          <View style={styles.headerCellRight}>
            <Text style={styles.headerText}>{"marketplace"}</Text>
          </View>
        </View>
        <View style={styles.barcodeSection}>
          <StaticBarcode />
          <Text style={styles.trackingText}>Tracking Number {order?.order_number || order?.id}</Text>
        </View>
        <View style={styles.middleRow}>
          <View style={styles.logoColumn}>
            <DarazLogo />
            <Text style={styles.routingCode}>{order?.order_number || order?.id}</Text>
          </View>
          <View style={styles.detailsColumn}>
            <View style={styles.detailCell}>
              <Text style={styles.detailText}>{"STANDARD"}</Text>
            </View>
            <View style={styles.detailCell}>
              <Text style={styles.detailText}>{"0.5kg"}</Text>
            </View>
            <View style={styles.detailCell}>
              <Text style={styles.detailText}>{"HOME"}</Text>
            </View>
            <View style={styles.detailCell}>
              <Text style={styles.detailText}>{safeOrder.payment_method}</Text>
            </View>
            <View style={styles.detailCell}>
              <Text style={styles.detailText}>Amount: {safeOrder.total_amount}</Text>
            </View>
            <View style={styles.detailCellLast}>
              <Text style={styles.detailText}>Shipping: {order?.shipping_amount || 0}</Text>
            </View>
          </View>
        </View>
        <View style={styles.orderRow}>
          <Text style={styles.orderText}>
            <Text style={styles.boldText}>Order Number:</Text> {order?.order_number || order?.id}
          </Text>
        </View>
        <View style={styles.datesRow}>
          <View style={styles.dateLeft}>
            <Text style={styles.dateText}>
              Order Creation Date {formatDate(safeOrder.created_at)}
            </Text>
          </View>
          <View style={styles.dateRight}>
            <Text style={styles.dateText}>
              AWB Print Date {formatDate(safeOrder.created_at)}
            </Text>
          </View>
        </View>
        <View style={styles.bottomSection}>
          <View style={styles.qrSection}>
            <Image style={styles.qrCode} src={qrCodeUrl} />
            <Text style={styles.courierText}>{order?.order_number || order?.id}</Text>
          </View>
          <View style={styles.addressSection}>
            <View style={styles.addressBlock}>
              <Text>
                <Text style={styles.addressLabel}>Recipient </Text>
                {safeOrder.recipient.name}
              </Text>
              <Text>{order?.shipping_address}</Text>
              <Text>{safeOrder.recipient.address_line2}</Text>
              <Text style={styles.boldText}>
                {safeOrder.recipient.city_line}
              </Text>
              <Text>{safeOrder.recipient.area}</Text>
              <Text>Phone {safeOrder.recipient.phone}</Text>
            </View>
            <View style={styles.dividerLine} />
            <View style={styles.addressBlock}>
              <Text>
                <Text style={styles.addressLabel}>Sender </Text>
                {order?.warehouse_name || companyDetails?.name}
              </Text>
              <Text>{order?.warehouse_address || companyDetails?.address}</Text>
              <Text>{safeOrder.sender.address_line2}</Text>
              <Text>{safeOrder.sender.address_line3}</Text>
              <Text>Phone {order?.warehouse_phone || companyDetails?.phone}</Text>
            </View>
          </View>
        </View>
      </View>
    </Page>
  );
};

// --- MAIN EXPORTED COMPONENT ---
// It takes an array of orders and maps them to pages
const ShippingLabel = ({ orders = [], companyDetails }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #ddd",
        }}
      ></div>
      <div style={{ flex: 1 }}>
        <PDFViewer width="100%" height="100%">
          <Document>
            {orders.map((order, index) => (
              <SingleLabelPage
                key={index}
                order={order}
                companyDetails={companyDetails}
              />
            ))}
          </Document>
        </PDFViewer>
      </div>
      {/* <PDFDownloadLink
        document={
          <Document>
            {orders.map((order, index) => (
              <SingleLabelPage key={index} order={order} />
            ))}
          </Document>
        }
        fileName={fileName}
        style={{
          backgroundColor: "#ff6200",
          color: "white",
          padding: "10px 20px",
          textDecoration: "none",
          borderRadius: "5px",
          fontWeight: "bold",
        }}
      >
        {({ loading }) =>
          loading
            ? "Generating PDF..."
            : `📄 Download ${orders.length} Label(s)`
        }
      </PDFDownloadLink> */}
    </div>
  );
};

export default ShippingLabel;
